import { z } from "zod";

import { isStatOrUrgentPriority, SchedulingPriority } from "@/shared/models/referral";
import { formatFromUnderscoreToHumanReadable } from "@/src/lib/string";
import { validateObject, ObjectErrors } from "@/src/lib/utils/object-utils";
import { simpleDateSchema } from "@/src/models/common-schema";
import { ReferralDocumentType } from "@/src/models/constants/document-types";
import { LeadSource } from "@/src/models/constants/lead-source";
import {
  BODY_PART_PROMPT,
  DIAGNOSIS_CODE_PROMPT,
  BODY_PART_LATERALITY_PROMPT,
  SCHEDULING_PRIORITY_PROMPT,
} from "@/src/models/constants/prompt";
import { SERVICE_LINE_PROMPT, ServiceLine } from "@/src/models/constants/service-lines";
import { Specialty } from "@/src/models/constants/specialty";
import { Coverage, coverageSchema } from "@/src/models/coverage";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { DocumentDateSchema } from "@/src/models/documents/document-dates";
import { ExtractedDocumentFields } from "@/src/models/documents/extracted-document-fields";
import {
  commonProcedureTerminologySchema,
  referralPriorAuthorizationFieldsSchema,
} from "@/src/models/documents/prior-authorization/prior-authorization";
import { formatICD10CodeForDisplay } from "@/src/models/icd10";
import { Location, locationSchema } from "@/src/models/location";
import { Patient, patientSchema, patientShape } from "@/src/models/patient";
import type { PatientEngagementOutcome } from "@/src/models/patient-engagement";
import { Practitioner, practitionerSchema } from "@/src/models/practitioner";
import type { Section } from "@/src/models/section";
import { sectionShape } from "@/src/models/section-shape";

export const referralTypeDisplay: Record<ReferralDocumentType, string> = {
  [ReferralDocumentType.CONSENT_FOR_RECORDS_SPECIALIST]: "Consent for Records - Specialist",
  [ReferralDocumentType.REFERRAL]: "Referral",
  [ReferralDocumentType.REFERRAL_AUTH]: "Referral Auth",
  [ReferralDocumentType.REFERRAL_OUTSIDE]: "Ref from Outside Practice - New Patient",
  [ReferralDocumentType.REFERRAL_REQUEST]: "Referral Request",
  [ReferralDocumentType.REFERRAL_RESPONSE]: "Referral Response",
};
export { SchedulingPriority, isStatOrUrgentPriority };

export enum ReferralStatus {
  AWAITING_INFORMATION = "awaiting_information",
  CANCELLED = "cancelled",
  COMPLETED = "completed",
  DUPLICATE = "duplicate",
  EXPIRED = "expired",
  IN_OUTREACH = "in_outreach",
  IN_PROGRESS = "in_progress",
  NOT_STARTED = "not_started",
}

export enum ReferralResponseType {
  MISSED_SERVICES = "Missed Services",
  UPCOMING_APPOINTMENT = "Upcoming Appointment",
  UNABLE_TO_CONTACT = "Unable to Contact",
  REACHED_OUT_BUT_DENIED_APPOINTMENT = "Reached Out But Denied Appointment",
  PENDING_INSURANCE_VERIFICATION = "Pending Insurance Verification",
}

export enum ReferralIntakeType {
  MOTOR_VEHICLE_ACCIDENT = "Motor Vehicle Accident",
  STANDARD = "Standard",
  WORKERS_COMPENSATION = "Workers' Compensation",
}

const REFERRAL_STATUS_LABELS: Record<ReferralStatus, string> = {
  [ReferralStatus.AWAITING_INFORMATION]: "Awaiting Information",
  [ReferralStatus.CANCELLED]: "Closed",
  [ReferralStatus.COMPLETED]: "Converted",
  [ReferralStatus.DUPLICATE]: "Duplicate",
  [ReferralStatus.EXPIRED]: "Expired",
  [ReferralStatus.IN_OUTREACH]: "In Outreach",
  [ReferralStatus.IN_PROGRESS]: "Appointment Booked",
  [ReferralStatus.NOT_STARTED]: "Needs Scheduling",
};

export function referralStatusLabel(status: string): string {
  return REFERRAL_STATUS_LABELS[status as ReferralStatus] ?? formatFromUnderscoreToHumanReadable(status);
}

export const terminalReferralStatuses: ReferralStatus[] = [
  ReferralStatus.CANCELLED,
  ReferralStatus.COMPLETED,
  ReferralStatus.DUPLICATE,
  ReferralStatus.EXPIRED,
];

export const diagnosisSchema = z.object({
  diagnosisText: z.string().nullable(),
  diagnosisCode: z.string().describe(DIAGNOSIS_CODE_PROMPT).nullable(),
  diagnosisCodeType: z.string().nullable(),
});
export type DiagnosisItem = z.infer<typeof diagnosisSchema>;

export type Diagnosis = z.infer<typeof diagnosisSchema>;

export function getDiagnosisCodeDisplayName(diagnosis: Diagnosis, includeDiagnosisText: boolean = true): string {
  const formattedCode = formatICD10CodeForDisplay(diagnosis.diagnosisCode) || "Unknown Code";
  const text = includeDiagnosisText && diagnosis.diagnosisText ? ` - ${diagnosis.diagnosisText}` : "";

  return `${formattedCode}${text}`;
}

export const diagnosisListSchema = z.array(diagnosisSchema);

const baseReferralSchema = z.object({
  patient: patientSchema,
  dateOfReferral: simpleDateSchema.nullable(),
  reasonForReferral: z
    .string()
    .nullable()
    .describe(
      `
     Follow these steps:

      1. Look for a heading line (case-insensitive) that begins with "Reason for Referral", "Referral Reason", "Reason for consultation", "Reason" and grab all text after the colon or dash on that line.  
      2. If no heading is found, scan for narrative cues—phrases like "Referred for", "For evaluation of", or "Due to" and capture the remainder of that clause up to the next period or line break.  
      3. Return exactly the captured text, without any extra punctuation, headings, or line breaks.

      Examples your parser should produce:
      - Input: "Reason for Referral: Evaluation of chronic back pain."  
        Output: Evaluation of chronic back pain

      - Input: "Referral Reason – Rule out appendicitis"  
        Output: Rule out appendicitis

      - Input: "The patient was Referred for MRI due to persistent headaches."  
        Output: MRI due to persistent headaches

      - Input: "For evaluation of abnormal liver function tests."  
        Output: evaluation of abnormal liver function tests

      - Input: "referring this patient for a MRI"
        Output: MRI

    `
    ),
  receivingDepartment: z.string().nullable(),
  serviceLine: z.nativeEnum(ServiceLine).nullable().describe(SERVICE_LINE_PROMPT),
  leadSource: z
    .nativeEnum(LeadSource)
    .nullable()
    .describe(
      `
      Lead source of the referral, if not sure, default to "Physician Referral".
      - "Facility Partners" will come from living facility and looks like an intake, there is usually a photo in the upper left corner or a space for a photo and the phrase "Admission" at the top.
      - "Community Outreach Event" has a footer form with United Vein on the bottom left corner with the logo and on the right phone, fax, email and website. There will be an event written at the top.
      - "PL Direct" will usually have the words "PL Direct" hand written on the referral. these look like Community Outreach Events
      `
    ),
  diagnosisCodeType: z.string().nullable(),
  diagnosisList: diagnosisListSchema.nullable(),
  schedulingPriority: z.nativeEnum(SchedulingPriority).nullable().describe(SCHEDULING_PRIORITY_PROMPT),
  numberOfVisits: z
    .number()
    .int()
    .nonnegative()
    .nullable()
    .describe(
      "The number of visits, sessions, or treatments authorized or requested by this referral. Look for phrases like 'X visits', 'X sessions', 'authorized for N visits', 'approved for N treatments', 'X units'. Return only the integer count. If not specified, return null."
    ),
  specialty: z.nativeEnum(Specialty).nullable(),
  specialties: z.array(z.nativeEnum(Specialty)).nullable(),
  clinicName: z.string().nullable(),
  containsCptCode: z.boolean().nullable(),
  commonProcedureTerminologyList: z
    .array(commonProcedureTerminologySchema)
    .nullable()
    .describe(
      "CPT/HCPCS procedure or service codes referenced in the document: one entry per code with the code, full text description, and modifier when shown. Use null if no billable procedure codes appear."
    ),
  referringProvider: practitionerSchema.nullable(),
  referringLocation: locationSchema.nullable(),
  receivingProvider: practitionerSchema.nullable(),
  receivingLocation: locationSchema.nullable(),
  primaryCareProvider: practitionerSchema.nullable(),
  provider: practitionerSchema.nullable(),
  primaryCoverage: coverageSchema.nullable(),
  secondaryCoverage: coverageSchema.nullable(),
  authorizationNumber: z
    .string()
    .nullable()
    .describe(
      `Authorization or referral number associated with the patient's insurance or referral.

     Look for the value next to or below labels like:
     - "Authorization", "Auth #", "Auth Number", "Pre-Auth", "Prior Auth"
     - "Referral #", "Referral Number", "Ref #", "Ref Number"
     - "Certification Number", "Cert #"

     Return only the alphanumeric identifier (may include hyphens).
     Do not confuse with Policy ID, Group Number, Member ID, or Subscriber ID.`
    ),
  referralResponse: z.nativeEnum(ReferralResponseType).nullable(),
  authExpired: z.boolean().nullable(),
  cannotServicePatient: z.boolean().nullable(),
  isAddCptRequest: z.boolean().nullable(),
  isDxChangeRequest: z.boolean().nullable(),
  isEarlySteps: z.boolean().nullable(),
  isInsuranceFollowUp: z.boolean().nullable(),
  isOtherReferralReason: z.boolean().nullable(),
  recordsRequested: z.boolean().nullable(),
  cde: z.boolean().nullable(),
  documentType: z.string().nullable(),
  isVaMilitaryReferral: z.boolean().nullable(),
  isInmate: z
    .boolean()
    .nullable()
    .optional()
    .describe(
      "True when the referral or intake document indicates the patient is currently incarcerated / an inmate " +
        "(e.g. mentions of a correctional facility, DOC, jail, prison, or a corrections officer as contact). " +
        "False or null otherwise."
    ),
  referralType: z.nativeEnum(ReferralIntakeType).nullable().optional(),
  isHospitalFollowUp: z
    .boolean()
    .nullable()
    .optional()
    .describe(
      "True when the document clearly indicates this referral is for a hospital follow-up — " +
        "e.g. a discharge follow-up, post-hospitalization follow-up, or follow-up after an " +
        "inpatient stay/admission or ER visit. False or null when there is no indication this " +
        "is a hospital follow-up."
    ),
  mentionsImaging: z
    .boolean()
    .nullable()
    .optional()
    .describe(
      "True when the referral mentions or includes imaging/diagnostic studies that have already been completed (e.g. MRI, CT, X-ray, ultrasound, EMG). False or null when no imaging is mentioned."
    ),
  companyName: z
    .string()
    .nullable()
    .optional()
    .describe("Name of the facility or company where the mentioned imaging was completed, if stated."),
  bodyPart: z.string().nullable().optional().describe(BODY_PART_PROMPT),
  bodyParts: z
    .array(z.string())
    .nullable()
    .optional()
    .describe("Every distinct body part the referral is for. Return null when no body part can be determined."),
  bodyPartLaterality: z.string().nullable().optional().describe(BODY_PART_LATERALITY_PROMPT),
  ...DocumentDateSchema.shape,
});

export const referralCoreMinimalSchema = baseReferralSchema.pick({
  dateOfReferral: true,
  dateReceived: true,
  reasonForReferral: true,
  receivingDepartment: true,
  serviceLine: true,
  leadSource: true,
  diagnosisCodeType: true,
  schedulingPriority: true,
  numberOfVisits: true,
  diagnosisList: true,
  containsCptCode: true,
  authorizationNumber: true,
  dateOfDocument: true,
  specialty: true,
  isVaMilitaryReferral: true,
  isInmate: true,
  referralType: true,
  isHospitalFollowUp: true,
  mentionsImaging: true,
  companyName: true,
  dateOfEvent: true,
  bodyPart: true,
  bodyParts: true,
  bodyPartLaterality: true,
});
export const referralProviderMinimalSchema = baseReferralSchema.pick({
  referringProvider: true,
  referringLocation: true,
  receivingProvider: true,
  receivingLocation: true,
  primaryCareProvider: true,
  primaryCoverage: true,
  secondaryCoverage: true,
});
export const referralPatientMinimalSchema = baseReferralSchema.pick({
  patient: true,
});

export const referralSubsetSchema = referralCoreMinimalSchema
  .merge(referralProviderMinimalSchema)
  .merge(referralPatientMinimalSchema);

export type ReferralSubset = z.infer<typeof referralSubsetSchema>;

// Custom fields for each referral document type
const referralCustomFields: Record<ReferralDocumentType, Record<string, z.ZodTypeAny>> = {
  [ReferralDocumentType.REFERRAL]: {
    ...referralCoreMinimalSchema.shape,
    ...referralProviderMinimalSchema.shape,
    ...referralPatientMinimalSchema.shape,
    ...referralPriorAuthorizationFieldsSchema.shape,
  },
  [ReferralDocumentType.REFERRAL_AUTH]: {
    ...referralCoreMinimalSchema.shape,
    ...referralProviderMinimalSchema.shape,
    ...referralPatientMinimalSchema.shape,
    ...referralPriorAuthorizationFieldsSchema.shape,
  },
  [ReferralDocumentType.REFERRAL_OUTSIDE]: baseReferralSchema.pick({
    patient: true,
    dateReceived: true,
  }).shape,
  [ReferralDocumentType.REFERRAL_REQUEST]: baseReferralSchema.pick({
    patient: true,
    dateOfDocument: true,
    dateReceived: true,
    specialty: true,
    specialties: true,
    diagnosisList: true,
    reasonForReferral: true,
    referringProvider: true,
    referringLocation: true,
    clinicName: true,
    receivingProvider: true,
    receivingLocation: true,
    provider: true,
    primaryCoverage: true,
    schedulingPriority: true,
    serviceLine: true,
    receivingDepartment: true,
    containsCptCode: true,
    commonProcedureTerminologyList: true,
    authorizationNumber: true,
    leadSource: true,
    dateOfReferral: true,
    authExpired: true,
    cannotServicePatient: true,
    isAddCptRequest: true,
    isDxChangeRequest: true,
    isEarlySteps: true,
    isInsuranceFollowUp: true,
    isOtherReferralReason: true,
    recordsRequested: true,
    cde: true,
    isVaMilitaryReferral: true,
  }).shape,
  [ReferralDocumentType.CONSENT_FOR_RECORDS_SPECIALIST]: baseReferralSchema.pick({
    patient: true,
    dateOfDocument: true,
    specialty: true,
    diagnosisList: true,
    reasonForReferral: true,
    referringProvider: true,
    referringLocation: true,
  }).shape,
  [ReferralDocumentType.REFERRAL_RESPONSE]: {
    ...baseReferralSchema.pick({
      patient: true,
      dateOfReferral: true,
      dateReceived: true,
      dateOfEvent: true,
      specialty: true,
      clinicName: true,
      documentType: true,
      receivingProvider: true,
    }).shape,
    referralResponse: z.nativeEnum(ReferralResponseType),
  },
};

const makeReferralSchema = (type: ReferralDocumentType, customFields: Record<string, z.ZodTypeAny>) =>
  z.object({
    type: z.literal(type),
    ...customFields,
  });

export const referralSchemas: Record<ReferralDocumentType, z.ZodObject<any>> = (
  Object.values(ReferralDocumentType) as ReferralDocumentType[]
).reduce(
  (acc, type) => {
    acc[type] = makeReferralSchema(type, referralCustomFields[type]);

    return acc;
  },
  {} as Record<ReferralDocumentType, z.ZodObject<any>>
);

const referralSchemasArray = (Object.values(ReferralDocumentType) as ReferralDocumentType[]).map(
  (type) => referralSchemas[type]
) as [z.ZodObject<any>, ...z.ZodObject<any>[]];

export const anyReferralSchema = z.discriminatedUnion("type", referralSchemasArray);

/**
 * Fields whose runtime type on a persisted `Referral` differs from what the extraction schema
 * infers (relations are hydrated model objects, not the raw extracted shape), plus columns that
 * only exist on the DB row.
 */
type ReferralPersistedFields = {
  type: ReferralDocumentType;
  section: Section;
  patient: Patient;
  /** `referral.patient_id`; always populated on a persisted row, even when `patient` is not joined. */
  patientId: string | null;
  referringProvider: Practitioner | null;
  referringLocation: Location | null;
  receivingProvider: Practitioner | null;
  receivingLocation: Location | null;
  primaryCareProvider: Practitioner | null;
  primaryCoverage: Coverage | null;
  secondaryCoverage: Coverage | null;
  displayId: string | null;
  status: ReferralStatus;
  note: string | null;
  syncedToEhr: boolean | null;
  routedLocationId: string | null;
  /** Legacy single-diagnosis columns, superseded by `diagnosisList`. */
  diagnosisText: string | null;
  diagnosisCode: string | null;
};

/** See {@link ExtractedDocumentFields} for why the fields are reconstructed rather than inferred. */
type ReferralExtractedFields = Omit<
  ExtractedDocumentFields<ReferralDocumentType, typeof baseReferralSchema> &
    Partial<z.infer<typeof referralPriorAuthorizationFieldsSchema>>,
  keyof ReferralPersistedFields
>;

export type Referral = ReferralExtractedFields & ReferralPersistedFields & DatabaseDefaults;

const hospitalFollowUpReferralSchema = referralCoreMinimalSchema.pick({ isHospitalFollowUp: true });

/** Reads the reviewed referral flag used as the authoritative Hospital FU discriminator. */
export function isReferralHospitalFollowUp(referral: Pick<Referral, "section"> | null): boolean {
  if (!referral) return false;

  const reviewedContent = hospitalFollowUpReferralSchema.safeParse(referral.section?.reviewedContent);

  return reviewedContent.success ? (reviewedContent.data.isHospitalFollowUp ?? false) : false;
}

export type PartialReferral = Partial<Referral>;

/**
 * Type for patient engagement row from database
 */
export type PatientEngagementRow = {
  id: string;
  deleted_at: string | null;
  direction: string | null;
  type?: string;
  note?: string | null;
  time_engaged?: string;
  user_id?: string;
  user_name?: string | null;
};

/**
 * Paginated referral with engagement data included
 * Used for list views where we need engagement counts
 */
export type ReferralFirstAppointment = {
  scheduledFor: string | null;
  scheduledOn: string | null;
  scheduledBy: string | null;
  status: string | null;
};

export type PaginatedReferral = Referral & {
  /** Denormalized outbound engagement count from referral.outreach_count. */
  outreachCount: number;
  /** Only populated by the export query; the list select omits engagements. */
  patientEngagement: PatientEngagementRow[];
  ehrDocumentId: string | null;
  firstAppointment: ReferralFirstAppointment | null;
  daysToSchedule: number | null;
  latestEngagementOutcome: PatientEngagementOutcome | null;
  note: string | null;
};

export const referralShape: Referral = {
  id: "",
  groupId: "",
  type: ReferralDocumentType.REFERRAL,
  dateOfReferral: null,
  dateReceived: null,
  reasonForReferral: null,
  receivingDepartment: null,
  diagnosisText: null,
  diagnosisCode: null,
  diagnosisCodeType: null,
  diagnosisList: null,
  patient: patientShape,
  patientId: null,
  section: sectionShape,
  referringProvider: null,
  referringLocation: null,
  receivingProvider: null,
  receivingLocation: null,
  primaryCareProvider: null,
  primaryCoverage: null,
  secondaryCoverage: null,
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
  schedulingPriority: SchedulingPriority.ROUTINE,
  numberOfVisits: null,
  displayId: null,
  serviceLine: null,
  leadSource: null,
  containsCptCode: false,
  authorizationNumber: null,
  referralResponse: null,
  status: ReferralStatus.NOT_STARTED,
  note: null,
  syncedToEhr: null,
  routedLocationId: null,
};

// Legacy exports for backward compatibility
export const referralSchema = referralSchemas[ReferralDocumentType.REFERRAL];

export type ReferralErrors = ObjectErrors<ReferralSubset>;
export type ReferralFieldKey = keyof ReferralSubset;

/**
 * Validates a Referral object using the generic validateObject helper.
 * If fieldsToValidate is provided, only those fields are validated.
 */
export function validateReferral(referral: Referral, fieldsToValidate?: ReferralFieldKey[]) {
  return validateObject<Referral>(baseReferralSchema, referral, fieldsToValidate);
}
